<!--
// nuxt-ui/pages/[region]/SquadSimulator.vue
-->
<script setup lang="ts" xmlns="http://www.w3.org/1999/html">
import type { Player } from "~/src/player/domain/Player";
import type { Team } from "~/src/team/domain/models/Team";
import { useTeams } from "~/nuxt-ui/Shared/composables/teams";
import { useRegions } from "~/nuxt-ui/Shared/composables/regions";
import { useSquadSimulator } from "~/nuxt-ui/Shared/composables/squadSimulator";
import { useWebContext } from "~/nuxt-ui/Shared/composables/webContext";
import { orderTeamsAlphabetically } from "~/src/team/domain/services/teamService";
import { useCompetitionStore } from "~/nuxt-ui/stores/competitionStore";
import { scrollToTop, setAliasUrl, slugify } from "~/src/Shared/utils";
import { initDropdowns } from "flowbite";
import CardTeamSquadSimulator from "~/nuxt-ui/components/pages/region-squad-simulator/CardTeamSquadSimulator.vue";
import CardPlayerSquadSimulator from "~/nuxt-ui/components/pages/region-squad-simulator/CardPlayerSquadSimulator.vue";
import BaseModal from "~/nuxt-ui/components/base/BaseModal.vue";
import DropDownIcon from "~/nuxt-ui/components/Shared/icons/DropDownIcon.vue";
import ResetIcon from "~/nuxt-ui/components/Shared/icons/ResetIcon.vue";
import DownloadIcon from "~/nuxt-ui/components/Shared/icons/DownloadIcon.vue";
import BaseSpinner from "~/nuxt-ui/components/base/BaseSpinner.vue";
import HeaderTitleCentered from "~/nuxt-ui/components/Shared/HeaderTitleCentered.vue";

const { t } = useI18n();
const localePath = useLocalePath();
const { currentSeasonTeams, currentSeason, region } = useCompetitionStore();
const { isSpain, isAmericas, isWorldCup } = useRegions();
const { isKings, isQueens, contextSiteTitle, contextDomain } = useWebContext();
const { getSeasonTeamPlayers } = useTeams();
const {
  MIN_PLAYERS,
  createPlaceholderPlayer,
  addPlaceholderToMaxPlayers,
  updateTeamBudget,
  isMinPlayers,
  isMaxPlayers,
  isPlayerMinLimitReached,
  isPlayerMaxLimitReached,
  filterPlayersWithMarketValues,
} = useSquadSimulator();

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/squad-simulator",
    es: "/[region]/simulador-plantilla",
    de: "/[region]/squad-simulator",
    fr: "/[region]/squad-simulator",
    it: "/[region]/squad-simulator",
    pt: "/[region]/squad-simulator",
  },
});

definePageMeta({
  middleware: [
    "not-found-italy",
    "not-found-americas",
    "not-found-world-cup",
    "not-found-nations",
    "not-found-king-of-kings-nations",
    "not-found-brazil",
    "not-found-france",
    "not-found-germany",
  ],
});

useHead({
  script: [
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/dom-to-image/2.6.0/dom-to-image.min.js",
      tagPosition: "head",
      tagPriority: "high",
    },
  ],
});

const selectedTeam = ref<Team | undefined>();
const teamBudget = ref<number | undefined>();

const originalTeamPlayers = ref<Player[]>([]);
const modifiedTeamPlayers = ref<Player[]>([]);

const isDownloading = ref<boolean>(false);
const showModal = ref<boolean>(false);
const isPortrait = ref<boolean>(false);

const selectedForExchange = ref<Player | undefined>();
const isLimitReached = ref<boolean>(false);

const exchangeTeamSelected = ref<Team | undefined>();
const squadExchangeTeamSelected = ref<Player[]>();
const selectedTeamCardsContainer = ref<HTMLElement | null>(null);
const swingPlayerCards = ref<boolean>(false);

const modalInfo = ref<{ visible: boolean; message: string }>({ visible: false, message: "" });

const activeTeams: Team[] = currentSeasonTeams.filter(t => !t.isPlaceholder);

const teamsAvailableForExchange = computed((): Team[] => {
  return selectedTeam.value ? activeTeams.filter(t => t.id !== selectedTeam.value?.id) : [];
});

const downloadPlayers = computed((): Player[] => {
  return modifiedTeamPlayers.value.filter(p => p.id > 0);
});

const leagueTitle = region?.leagueTitle || contextSiteTitle;

const currentImage = region?.footerLogo || undefined;

const restart = () => {
  selectedTeam.value = undefined;
  teamBudget.value = undefined;
  originalTeamPlayers.value = [];
  modifiedTeamPlayers.value = [];
  squadExchangeTeamSelected.value = [];
  exchangeTeamSelected.value = undefined;
  selectedForExchange.value = undefined;
  isLimitReached.value = false;

  scrollToTop(200);
};

const showAlert = (message: string): void => {
  modalInfo.value = { visible: true, message };
};

const isPlayerInModifiedTeam = (playerId: number): boolean => {
  return modifiedTeamPlayers.value.some(player => player.id === playerId);
};

const handleSellPlayer = (player: Player) => {
  if (selectedTeam.value) {
    if (isMinPlayers(modifiedTeamPlayers.value)) {
      showAlert(t("squadSimulator.alert.minSquadSize"));
    } else {
      const updatedPlayers = modifiedTeamPlayers.value.map(p =>
        p.id === player.id ? createPlaceholderPlayer() : p
      );
      modifiedTeamPlayers.value = updatedPlayers;
      if (teamBudget.value && player.marketValue) {
        teamBudget.value = updateTeamBudget(teamBudget.value, player.marketValue);
      }
      isLimitReached.value = isMaxPlayers(updatedPlayers);
    }
  }
};
const handleSelectForExchange = (player: Player) => {
  selectedForExchange.value = player;
  const exchangeSection = document.getElementById("exchange-section");

  if (exchangeSection) {
    const elementPosition = exchangeSection.getBoundingClientRect().top + window.scrollY;
    const headerOffset = 200;
    const scrollPosition = elementPosition - headerOffset;

    window.scrollTo({
      top: scrollPosition,
      behavior: "smooth",
    });
  }
};

const handleRecruitPlayer = (player: Player) => {
  if (isMaxPlayers(modifiedTeamPlayers.value)) {
    showAlert(t("squadSimulator.alert.maxSquadSize"));
    isLimitReached.value = true;
  } else {
    isLimitReached.value = false;
    if (selectedForExchange.value && selectedTeam.value) {
      const marketValue = player.marketValue;
      if (marketValue) {
        if (teamBudget.value !== undefined && teamBudget.value >= marketValue) {
          teamBudget.value = updateTeamBudget(teamBudget.value, -marketValue);
          modifiedTeamPlayers.value = modifiedTeamPlayers.value.map(p =>
            p.id === selectedForExchange.value.id
              ? {
                  ...player,
                  image: player.image,
                  shortName: player.shortName,
                  role: player.role,
                  jersey: player.jersey,
                  marketValue: player.marketValue,
                }
              : p
          );
          selectedForExchange.value = undefined;
          //check after recruiting
          isLimitReached.value = !isMaxPlayers(modifiedTeamPlayers.value);
        } else {
          showAlert(t("squadSimulator.alert.insufficientBudget"));
        }
        scrollToTop(200);
      }
    }
  }
};

const handleResetPlayer = (playerIndex: number) => {
  if (selectedTeam.value) {
    const originalPlayer = originalTeamPlayers.value[playerIndex];
    const recruitPlayer = modifiedTeamPlayers.value[playerIndex];

    const transaction = recruitPlayer.marketValue - originalPlayer.marketValue;

    modifiedTeamPlayers.value = modifiedTeamPlayers.value.map((p, index) =>
      index === playerIndex ? originalPlayer || createPlaceholderPlayer() : p
    );

    if (!isPlayerMaxLimitReached(modifiedTeamPlayers.value)) {
      if (teamBudget.value !== undefined) {
        teamBudget.value = updateTeamBudget(teamBudget.value, transaction);
      }
      isLimitReached.value = false;
    } else {
      isLimitReached.value = true;
      showAlert(t("squadSimulator.alert.exceedingMaxSquadSize"));
    }
  }
};

const cancelRecruitProcess = () => {
  selectedForExchange.value = undefined;
};

const getTeamSquad = async (teamId: number, isExchangeTeam: boolean): Promise<void> => {
  try {
    const squad = (await getSeasonTeamPlayers(currentSeason.id, teamId)) || [];
    const filteredPlayers = filterPlayersWithMarketValues(squad);

    if (isExchangeTeam) {
      squadExchangeTeamSelected.value = filteredPlayers;
    } else {
      originalTeamPlayers.value = filteredPlayers;

      if (isPlayerMaxLimitReached(originalTeamPlayers.value)) {
        showAlert(t("squadSimulator.alert.exceedingMaxSquadSize"));
        isLimitReached.value = true;
      } else if (isPlayerMinLimitReached(originalTeamPlayers.value)) {
        const number = MIN_PLAYERS - originalTeamPlayers.value.length;
        showAlert(t("squadSimulator.alert.exceedingMinSquadSize", { number }));
        isLimitReached.value = true;
      }

      //Always show max spots
      modifiedTeamPlayers.value = addPlaceholderToMaxPlayers([...originalTeamPlayers.value]);

      const selectedTeam = activeTeams.find(t => t.id === teamId);
      teamBudget.value = selectedTeam?.budget;

      if (teamsAvailableForExchange.value) {
        const teamToGet = activeTeams[0] === selectedTeam ? activeTeams[1] : activeTeams[0];
        await getSelectedExchangeTeamSquad(teamToGet);
      }
    }
  } catch (error) {
    console.error(`Failed to get ${isExchangeTeam ? "exchange team" : "selected team"} squad:`, error);
  } finally {
    initDropdowns();
  }
};

const showDownloadButton = computed(() => {
  return (
    !isPlayerMinLimitReached(modifiedTeamPlayers.value) && !isPlayerMaxLimitReached(modifiedTeamPlayers.value)
  );
});

const getSelectedTeamSquad = async (teamId: number): Promise<void> => {
  if (window && window.innerWidth < 1280) {
    swingPlayerCards.value = true;
    setTimeout(() => {
      swingPlayerCards.value = false;
    }, 5000);
  }

  await getTeamSquad(teamId, false);
  scrollToTop(200);
};

const getSelectedExchangeTeamSquad = async (team: Team): Promise<void> => {
  exchangeTeamSelected.value = team;
  await getTeamSquad(team.id, true);
};

const contextSiteTitleNoSpaces = contextSiteTitle.replace(/\s+/g, "");

const downloadTeamImage = () => {
  isDownloading.value = true;

  if (typeof window !== "undefined" && window.domtoimage) {
    const htmlSection = document.getElementById("SquadSimulatorCapture");

    if (htmlSection) {
      setTimeout(() => {
        domtoimage
          .toPng(htmlSection, {
            quality: 1,
            bgcolor: "#000",
          })
          .then(function (dataUrl) {
            const link = document.createElement("a");
            link.href = dataUrl;
            link.download = t("squadSimulator.capture.downloadName", { contextSiteTitleNoSpaces });
            link.click();
            link.remove();
            isDownloading.value = false;
            showModal.value = false;
          })
          .catch(function (error) {
            console.error("Error generating screenshot:", error);
            isDownloading.value = false;
            showModal.value = false;
          });
      }, 200);
    }
  }
};

const menuExchangeTeams = ref<HTMLElement | null>(null);
const dropdownExchangeTeams = ref<HTMLElement | null>(null);
const exchangePlayerCardsContainer = ref<HTMLElement | null>(null);

onMounted(() => {
  initDropdowns();
  document.addEventListener("mousedown", function (event: MouseEvent) {
    const menuExchangeTeamsClicked =
      menuExchangeTeams.value && menuExchangeTeams.value.contains(event.target as Node);
    const dropdownExchangeTeamsClicked =
      dropdownExchangeTeams.value && dropdownExchangeTeams.value.contains(event.target as Node);
    const exchangePlayerCardsContainerClicked =
      exchangePlayerCardsContainer.value && exchangePlayerCardsContainer.value.contains(event.target as Node);

    if (!menuExchangeTeamsClicked && !dropdownExchangeTeamsClicked && !exchangePlayerCardsContainerClicked) {
      cancelRecruitProcess();
    }
  });
});

onUnmounted(() => {
  document.removeEventListener("mousedown", cancelRecruitProcess);
});

useSeoMeta({
  title: t("meta.title.squadSimulator", { contextSiteTitle }),
  description: t("meta.description.squadSimulator", { leagueTitle }),
  ogTitle: t("meta.title.squadSimulator", { contextSiteTitle }),
  ogDescription: t("meta.description.squadSimulator", { leagueTitle }),
});

const simulatorTeamsItemList = activeTeams.map((team, index) => {
  const url = localePath({
    name: "teams-team-teamName",
    params: { team: team.id, teamName: slugify(team.name) },
  });
  return `{"@type": "ListItem","position": "${index + 1}","item": {"@type" : "SportsTeam", "name" : "${team?.name}", "additionalProperty":{ "@type": "PropertyValue", "name":"Budget", "value":"${team?.budget}",  "url":"https://${contextDomain + url}"}}}`;
});

useHead({
  script: () => [
    {
      type: "application/ld+json",
      innerHTML: `{"@context": "https://schema.org","@type": "ItemList","name": "${contextSiteTitle + " " + t("meta.title.squadSimulator")}", "itemListElement": [${simulatorTeamsItemList}]}`,
    },
  ],
});
</script>

<template>
  <header-title-centered
    :title="$t('squadSimulator.title')"
    :subtitle="$t('squadSimulator.subtitle', { contextSiteTitle })"
  ></header-title-centered>
  <section class="my-6 main-container">
    <!--    <div class="kql-w-container py-8 px-4 md:py-10 md:px-6">-->
    <!--      <h1 class="text-center text-3xl md:text-4xl font-extrabold text-gray-100 mb-6">-->
    <!--        <span class="relative inline-block"> {{}} </span>-->
    <!--      </h1>-->
    <!--    </div>-->

    <div class="flex flex-col gap-2 py-8 px-4 md:py-6 md:px-6 kql-w-container">
      <div
        :class="[
          'grid grid-cols-1 xl:grid-cols-3 gap-4 ',
          selectedTeam ? 'md:grid-cols-5' : 'md:grid-cols-2',
        ]"
      >
        <transition-group name="teams">
          <card-team-squad-simulator
            v-for="(team, i) in orderTeamsAlphabetically(activeTeams)"
            :key="team.id"
            :team="team"
            :teamBudget="teamBudget ? teamBudget : team.budget"
            :preload="i === 0"
            with-hover
            v-model:selected-team="selectedTeam"
            @update:selected-team="e => getSelectedTeamSquad(e.id)"
          ></card-team-squad-simulator>
        </transition-group>
      </div>
      <div v-if="selectedTeam" class="flex gap-2 justify-end md:px-12">
        <button
          class="btn btn-default flex gap-2 items-center justify-center md:justify-start w-full sm:w-auto"
          @click="restart"
        >
          <span class="ml-2"> <ResetIcon /> </span>
          <span class="hidden md:inline-block">{{ t("squadSimulator.button.restart") }}</span>
        </button>

        <!-- Modal for Orientation Preview -->
        <button
          class="btn flex gap-2 items-center justify-center w-full sm:w-auto"
          :class="!showDownloadButton ? 'btn-disabled' : 'btn-emphasis'"
          :disabled="!showDownloadButton"
          @click="showModal = true"
        >
          <span class="ml-2"> <DownloadIcon /> </span>
          <span class="hidden md:inline-block">{{ t("squadSimulator.button.download") }}</span>
        </button>
      </div>
    </div>

    <transition name="squad">
      <div v-show="modifiedTeamPlayers.length && selectedTeam" class="kql-w-container pl-4 md:px-6">
        <div class="overflow-x-scroll hide-scrollbar">
          <div
            ref="selectedTeamCardsContainer"
            :class="[
              'grid grid-cols-6 min-w-max justify-items-center gap-y-2 gap-x-2 pb-6 md:px-12',
              { swing: swingPlayerCards },
            ]"
          >
            <!-- Selected Team Players -->
            <card-player-squad-simulator
              v-for="(player, index) in modifiedTeamPlayers"
              :key="player.id"
              :player="player"
              :team="selectedTeam"
              :isPlaceholder="player.id < 0"
              :showResetButton="index < originalTeamPlayers.length"
              :showRecruitButton="!isMaxPlayers(modifiedTeamPlayers)"
              :isOriginalPlayer="originalTeamPlayers.some(p => p.id === player.id)"
              :isSelectedForExchange="selectedForExchange?.id === player.id"
              @sell-player="handleSellPlayer"
              @select-for-exchange="handleSelectForExchange"
              @reset-player="() => handleResetPlayer(index)"
            ></card-player-squad-simulator>
          </div>
        </div>

        <div id="exchange-section" class="min-w-max md:px-12 flex justify-between">
          <!--          List of teams to choose-->
          <button
            v-if="exchangeTeamSelected && teamsAvailableForExchange.length > 0"
            id="menuExchangeTeams"
            ref="menuExchangeTeams"
            data-dropdown-toggle="dropdownExchangeTeams"
            class="rounded-md bg-gray-800 px-3 py-2.5 flex justify-between items-center min-w-max"
            type="button"
          >
            <NuxtImg
              v-if="exchangeTeamSelected.logo"
              :src="setAliasUrl(exchangeTeamSelected.logo.url)"
              :alt="exchangeTeamSelected.shortName"
              width="30"
              height="30"
            />
            {{ exchangeTeamSelected.completeName }}
            <DropDownIcon class="w-2 h-2 ml-1" />
          </button>
          <!-- Dropdown menu -->
          <div
            id="dropdownExchangeTeams"
            ref="dropdownExchangeTeams"
            class="z-30 hidden rounded-lg shadow text-left bg-gray-900 border border-gray-700 min-w-max"
          >
            <ul class="flex flex-col" aria-labelledby="menuStatsNav">
              <li
                v-for="(team, i) in teamsAvailableForExchange"
                :key="team.id"
                class="flex justify-start items-center gap-2 px-2 py-1 hover:bg-gray-800 cursor-pointer transition-colors duration-200"
                :class="{
                  'hover:rounded-t-lg': i === 0,
                  'hover:rounded-b-lg': i === teamsAvailableForExchange.length - 1,
                }"
                @click="getSelectedExchangeTeamSquad(team)"
              >
                <NuxtImg
                  v-if="team.logo"
                  :src="setAliasUrl(team.logo.url)"
                  :alt="team.shortName"
                  width="30"
                  height="30"
                />
                {{ team.completeName }}
              </li>
            </ul>
          </div>
          <!--          Budget-->
          <div
            v-if="teamBudget"
            class="rounded-md mr-4 bg-gray-900 px-2 flex justify-between items-center gap-2"
          >
            <p class="font-semibold">{{ t("team.budget.title") }}</p>
            <p class="font-bold text-primary">{{ teamBudget }} M</p>
          </div>
        </div>

        <!-- List of exchange players -->
        <div class="overflow-x-scroll hide-scrollbar pt-2">
          <div
            class="flex justify-center items-center min-w-max md:min-w-min md:flex-wrap gap-3 scale-75 origin-top-left md:origin-top"
            ref="exchangePlayerCardsContainer"
          >
            <card-player-squad-simulator
              v-for="player in squadExchangeTeamSelected"
              :key="player.id"
              :player="player"
              :team="exchangeTeamSelected"
              isExchangeTeam
              loading-lazy
              :is-selectable="selectedForExchange && !isPlayerInModifiedTeam(player.id)"
              @recruit-player="handleRecruitPlayer"
            ></card-player-squad-simulator>
          </div>
        </div>
      </div>
    </transition>
    <BaseModal
      v-model="modalInfo.visible"
      modal-id="modalSquadSimulator"
      :message="modalInfo.message"
      isAlert
      showCloseButton
    />

    <!-- Instruction Section -->
    <div class="kql-w-container py-2 px-4 md:py-3 md:px-6">
      <div class="bg-gray-800 rounded-lg p-6">
        <p class="text-2xl font-bold text-center mb-4">{{ t("squadSimulator.instructions.title") }}</p>

        <ol class="list-decimal md:pl-6 space-y-2">
          <li class="flex items-center space-x-3">
            <span class="text-xl font-bold text-primary">1</span>
            <p class="text-base">{{ t("squadSimulator.instructions.step1") }}</p>
          </li>

          <li class="flex items-center space-x-3">
            <span class="text-xl font-bold text-primary">2</span>
            <div>
              <p class="text-base">{{ t("squadSimulator.instructions.step2") }}</p>
            </div>
          </li>

          <!-- Step 3 -->
          <li class="flex items-center space-x-3">
            <span class="text-xl font-bold text-primary">3</span>
            <div class="flex gap-2">
              <i18n-t keypath="squadSimulator.instructions.step3" tag="p" class="text-base">
                <template #hashtag>
                  <span class="font-bold text-primary">#{{ contextSiteTitleNoSpaces }}Simulator</span>
                </template>
              </i18n-t>
            </div>
          </li>
        </ol>
      </div>
    </div>
  </section>

  <!--  Download Image-->
  <BaseModal
    isPreview
    modalId="download-image-modal"
    :modelValue="showModal"
    @update:modelValue="showModal = $event"
  >
    <!-- Modal Body -->
    <template #body>
      <!-- Orientation Selection -->
      <div class="flex justify-center gap-4 py-4">
        <div
          :class="{
            'border-b-2 border-primary text-primary font-bold': !isPortrait,
            'text-gray-500': isPortrait,
          }"
          @click="isPortrait = false"
          class="cursor-pointer px-4 py-2 transition-colors"
        >
          {{ t("squadSimulator.orientation.landscape") }}
        </div>
        <div
          :class="{
            'border-b-2 border-primary text-primary font-bold': isPortrait,
            'text-gray-500': !isPortrait,
          }"
          @click="isPortrait = true"
          class="cursor-pointer px-4 py-2 transition-colors"
        >
          {{ t("squadSimulator.orientation.portrait") }}
        </div>
      </div>

      <!-- Preview Image based on Orientation -->
      <section
        class="origin-top-left scale-[0.25] h-[380px] w-[380px]"
        :class="{
          '-ml-2': !isPortrait,
          'pl-40': isPortrait,
        }"
      >
        <div
          id="SquadSimulatorCapture"
          class="flex flex-col pt-8 px-8 pb-10"
          :class="{
            'w-[1200px] h-[800px]': !isPortrait,
            'w-[800px] h-[1200px]': isPortrait,
          }"
        >
          <div class="flex justify-between items-center gap-4 px-6 pt-6">
            <div>
              <!--              Context League Logo-->
              <img
                :src="currentImage.src"
                :alt="currentImage.alt"
                :width="currentImage.width"
                :height="currentImage.height"
              />
            </div>
            <div class="text-right">
              <p class="text-xl font-bold">
                {{ t("squadSimulator.capture.text1") }}
                <span class="ml-3">🔥💪</span>
              </p>
              <p class="text-lg font-bold">{{ t("squadSimulator.capture.text2", { contextSiteTitle }) }}</p>
              <p>{{ t("squadSimulator.capture.text3") }}</p>
              <p class="text-end">
                <span class="font-bold text-primary">#{{ contextSiteTitleNoSpaces }}Simulator</span>
              </p>
            </div>
          </div>

          <div class="flex flex-col gap-2 px-4 md:px-8 py-6 mx-auto">
            <!-- Selected Team -->
            <card-team-squad-simulator
              v-if="selectedTeam"
              isDownload
              :key="selectedTeam.id"
              :team="selectedTeam"
              :teamBudget="teamBudget ? teamBudget : selectedTeam.budget"
              v-model:selected-team="selectedTeam"
            ></card-team-squad-simulator>

            <div class="flex flex-wrap justify-center gap-4">
              <!-- Modified Team Players -->
              <card-player-squad-simulator
                v-for="player in downloadPlayers"
                :key="player.id"
                :player="player"
                :team="selectedTeam"
                isDownload
              />
            </div>
          </div>
        </div>
      </section>
      <i18n-t keypath="squadSimulator.capture.share" tag="p" class="text-end px-4 pb-4 -mt-4">
        <template #hashtag>
          <span class="font-bold text-primary">#{{ contextSiteTitleNoSpaces }}Simulator</span>
        </template>
      </i18n-t>
    </template>

    <!-- Modal Footer -->
    <template #footer>
      <div class="flex items-center justify-evenly gap-4 mb-6 -mt-8">
        <button class="btn btn-muted" @click="showModal = false">
          {{ t("squadSimulator.button.cancel") }}
        </button>
        <button
          class="btn flex items-center justify-center gap-2 md:justify-start"
          :class="isDownloading ? 'btn-muted' : 'btn-emphasis'"
          @click="downloadTeamImage"
        >
          <BaseSpinner class="!fill-black" :size="25" v-if="isDownloading" />
          {{ t("squadSimulator.button.confirm") }}
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<style scoped>
.teams-enter-active,
.teams-leave-active,
.teams-move {
  transition: all 0.6s ease;
}

.teams-enter-from,
.teams-leave-from {
  transform: translateY(-10px);
  opacity: 0;
}

.teams-leave-active {
  position: absolute;
}

.squad-enter-active,
.squad-leave-active {
  transition: all 0.8s ease;
}

.squad-enter-from,
.squad-leave-from {
  opacity: 0;
}

.swing {
  position: relative;
  animation: swing 5s 5 ease-in-out;
}

@keyframes swing {
  18% {
    transform: translateX(0);
  }
  24% {
    transform: translateX(-20px);
  }
  30% {
    transform: translateX(0);
  }
  36% {
    transform: translateX(-20px);
  }
  42% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
